<template>
  <div
    class="controls-container"
    :class="{
      'active-slide': activeElementType === 'slide',
      'active-tag': activeElementType === 'tag',
      'active-marker': activeElementType === 'marker',
      'active-polygon': activeElementType === 'polygon',
    }"
  >
    <ActionsMenu
      :action-index="actionIndex"
      :action-count="actions.length"
      :active-element="activeElement"
      :active-element-type="activeElementType"
      :selected-elements-count="selectedElements.length"
      @zoom-in="zoomIn"
      @zoom-out="zoomOut"
      @action-undo="actionUndo"
      @action-redo="actionRedo"
      @align-horizontal="alignHorizontal"
      @align-vertical="alignVertical"
      @distribute-horizontal="distributeHorizontal"
      @distribute-vertical="distributeVertical"
    />

    <MenuWrapper :is-active="isEditMenuActive">
      <TagMenu
        v-if="activeElementType === 'tag' && selectedElements.length === 1"
        v-model="activeElement"
        :colour-history="sharedColourHistory"
        @remove="removeTag"
        @duplicate="duplicateTag"
      />

      <MarkerMenu
        v-if="
          activeElementType === 'marker' &&
          selectedElements.length === 1 &&
          !isActiveMarkerPropertyMarker
        "
        v-model="getActiveMarker"
        :colour-history="sharedColourHistory"
        @duplicate-marker="duplicateMarker"
        @remove-marker="removeMarker"
      />

      <PolygonMenu
        v-if="activeElementType === 'polygon'"
        v-model="activeElement"
        :colour-history="sharedColourHistory"
        :polygon-id="activeElement._id || activeElement.id"
      />
      <SlideMenu
        v-if="activeElementType === 'slide'"
        v-model="activeElement"
        @remove-slide="removeSlide"
      />
    </MenuWrapper>

    <!-- active element banner -->
    <div
      v-if="activeElementLabel"
      class="active-banner"
      :class="{
        'active-banner--tag': activeElementType === 'tag',
        'active-banner--slide': activeElementType === 'slide',
        'active-banner--marker': activeElementType === 'marker',
        'active-banner--polygon': activeElementType === 'polygon',
      }"
    >
      {{ activeElementLabel }}
    </div>
    <!-- /banner -->

    <div class="zoom-container">
      <div
        class="draggable-container"
        :class="`draggable-container--zoom-${zoomLevel}`"
      >
        <div
          v-if="['marker', 'tag', 'dot'].includes(activeElementType)"
          class="dot-status"
        >
          x:
          <input
            class="form-control"
            type="number"
            v-model="activeElement.pos.x"
          />
          y:
          <input
            class="form-control"
            type="number"
            v-model="activeElement.pos.y"
          />
        </div>

        <LivingOptionTags
          v-model="tags"
          :active-element="activeElement"
          :active-slide="activeSlide"
          :selected-elements="selectedElements"
          :zoom-level="zoomLevel"
          @record-action="recordAction"
          @record-before-action="updateBeforeAction"
          @select-all-of-type="selectAllOfType"
        />

        <LivingOptionMarkers
          :property-ids="propertyIds ? propertyIds : []"
          :subsection-ids="subsectionIds ? subsectionIds : []"
          :living-option-ids="livingOptionIds ? livingOptionIds : []"
          :selected-elements="selectedElements"
          :zoom-level="zoomLevel"
          @record-action="recordAction"
          @record-before-action="updateBeforeAction"
          @select-all-markers="selectAllMarkers"
        />

        <LivingOptionPolygons
          :property-ids="propertyIds ? propertyIds : []"
          :subsection-ids="subsectionIds ? subsectionIds : []"
          :living-option-ids="livingOptionIds ? livingOptionIds : []"
          :active-element="activeElement"
          :active-element-type="activeElementType"
          :zoom-level="zoomLevel"
          @record-before-action="updateBeforeAction"
          @record-action="recordAction"
        />

        <TagPolygons
          :tags="tags"
          :active-slide="activeSlide"
          :active-element="activeElement"
          :active-element-type="activeElementType"
          :zoom-level="zoomLevel"
          @record-before-action="updateBeforeAction"
          @record-action="recordAction"
        />

        <!-- Background image -->
        <div
          class="draggable-container__bg"
          :class="{ 'no-image': !effectiveBackgroundImage }"
          @click="deselectElement"
        >
          <img
            v-if="assetType(effectiveBackgroundImage) === 'image'"
            class="tag-bg"
            :class="{ hidden: effectiveBackgroundImage === '' }"
            :src="$store.state.assetsPath + effectiveBackgroundImage"
          />
          <video
            v-if="assetType(effectiveBackgroundImage) === 'video'"
            class="tag-bg"
            :class="{ hidden: effectiveBackgroundImage === '' }"
            :src="$store.state.assetsPath + effectiveBackgroundImage"
            autoplay
            muted
            :loop="shouldVideoLoop"
          ></video>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import { ObjectId } from "bson";
import MenuWrapper from "@/components/Draggable/MenuWrapper";
import ActionsMenu from "@/components/Draggable/ActionsMenu";
import TagMenu from "@/components/Draggable/TagMenu";
import MarkerMenu from "@/components/Draggable/MarkerMenu";
import PolygonMenu from "@/components/Draggable/PolygonMenu.vue";
import SlideMenu from "@/components/Draggable/SlideMenu.vue";
import LivingOptionTags from "@/components/LivingOptionTags";
import LivingOptionMarkers from "@/components/LivingOptionMarkers";
import LivingOptionPolygons from "@/components/LivingOptionPolygons";
import TagPolygons from "@/components/TagPolygons";
import TagEditMenu from "@/components/TagEditMenu";
import MarkerEditMenu from "@/components/MarkerEditMenu";
import PolygonEditMenu from "@/components/PolygonEditMenu";

export default {
  props: [
    "tags",
    "propertyIds",
    "subsectionIds",
    "livingOptionIds",
    "backgroundImage",
  ],

  components: {
    MenuWrapper,
    TagMenu,
    MarkerMenu,
    PolygonMenu,
    SlideMenu,
    ActionsMenu,
    LivingOptionTags,
    LivingOptionMarkers,
    LivingOptionPolygons,
    TagPolygons,
    TagEditMenu,
    MarkerEditMenu,
    PolygonEditMenu,
  },

  data() {
    return {
      zoomLevel: 1,
      maxZoomLevel: 4,

      actionIndex: -1,
      actions: [],
      beforeAction: {},

      sharedColourHistory: [],
    };
  },

  computed: {
    ...mapGetters({
      activeLanguage: "activeLanguage",
      getActiveElement: "draggable/getActiveElement",
      getActiveMarker: "draggable/getActiveMarker",
      activeSlide: "draggable/getActiveSlide",
      getSelectedElements: "draggable/getSelectedElements",
      page: "page",
      isMenuOpen: "draggable/getIsMenuOpen",
    }),

    ...mapState({
      livingOptions: (state) => state.living.livingOptions,
      subsections: (state) => state.living.subsections,
      properties: (state) => state.living.properties,
      configItems: (state) => state.config.configItems,
      markerIndex: (state) => state.draggable.markerIndex,
    }),

    activeElement: {
      get: function () {
        return this.getActiveElement ? this.getActiveElement.element : null;
      },

      set(newElement) {
        this.$store.commit("draggable/addToSelectedElements", {
          element: newElement,
          elementType: this.activeElementType,
          isSingle: true,
        });
      },
    },

    selectedElements: {
      get: function () {
        return this.getSelectedElements;
      },

      set(newElements) {
        console.log("setting selected elements", newElements);
      },
    },

    activeElementType() {
      return this.getActiveElement ? this.getActiveElement.type : "";
    },

    isActiveMarkerPropertyMarker() {
      if (this.activeElementType !== "marker") return false;
      if (!this.activeElement || !this.activeElement._id) return false;

      // if the selected marker's _id matches a property _id, it’s a property marker
      return (this.properties || []).some(
        (p) => p && p._id === this.activeElement._id
      );
    },

    isActiveMarkerPropertyDot() {
      if (this.activeElementType !== "dot") return false;
      if (!this.activeElement || !this.activeElement._id) return false;

      // if the selected marker's _id matches a property _id, it’s a property marker
      return (this.properties || []).some(
        (p) => p && p._id === this.activeElement._id
      );
    },

    isEditMenuActive() {
      const types = ["tag", "marker", "polygon", "slide"];
      if (!types.includes(this.activeElementType)) return false;

      // tags - only when exactly 1 selected
      if (this.activeElementType === "tag") {
        return this.selectedElements.length === 1;
      }

      // markers
      if (this.activeElementType === "marker") {
        // property marker -> never editable
        if (this.isActiveMarkerPropertyMarker) return false;

        // subsection/living option markers -> only when 1 selected
        return this.selectedElements.length === 1;
      }

      return true;
    },

    effectiveBackgroundImage() {
      // if there is an active slide with a background override, use that
      if (this.activeSlide && this.activeSlide.backgroundImageOverride) {
        return this.activeSlide.backgroundImageOverride;
      }

      // otherwise, use the page background for the current language (if available)
      if (this.page && this.page.backgroundImage) {
        const langCode =
          this.activeLanguage && this.activeLanguage.code
            ? this.activeLanguage.code
            : "en";

        const langBg =
          this.page.backgroundImage[langCode] ||
          this.page.backgroundImage.en ||
          "";

        if (langBg) {
          return langBg;
        }
      }

      // Fallback: the prop passed from PageEdit (page.backgroundImage.en)
      return this.backgroundImage || "";
    },

    shouldVideoLoop() {
      if (this.assetType(this.backgroundImage) === "image") {
        return false;
      }

      if (this.page && this.page.backgroundVideoSettings.loop) {
        return true;
      }

      if (this.$route.name === "Masterplan") {
        const videoSettings = this.configItems.find(
          (item) => item.key === "liveHereBackgroundVideoSettings"
        );
        if (videoSettings) {
          return videoSettings.value.loop;
        }
      }

      return false;
    },

    activeElementLabel() {
      if (!this.activeElement) return "";

      const type = this.activeElementType;
      const langCode =
        this.activeLanguage && this.activeLanguage.code
          ? this.activeLanguage.code
          : "en";

      // Only show labels for slide, tag, marker
      if (!["slide", "tag", "marker", "polygon"].includes(type)) return "";

      const parts = [];

      // --- SLIDE ---
      const hasActiveSlide =
        type === "slide" || (this.activeSlide && this.activeSlide._id);

      if (hasActiveSlide) {
        const slide = type === "slide" ? this.activeElement : this.activeSlide;

        if (slide && slide.label) {
          const slideLabel =
            slide.label[langCode] || slide.label.en || slide._id;
          parts.push("[SLIDE] " + slideLabel);
        }
      }

      // --- TAG ---
      if (type === "tag") {
        let tagLabel = "";

        if (this.activeElement.label) {
          tagLabel =
            this.activeElement.label[langCode] ||
            this.activeElement.label.en ||
            "";
        } else if (this.activeElement.name) {
          tagLabel = this.activeElement.name;
        } else if (this.activeElement.title) {
          tagLabel = this.activeElement.title;
        }

        parts.push("[TAG] " + (tagLabel || this.activeElement._id));
      }

      // --- MARKER ---
      if (type === "marker") {
        const el = this.activeElement;
        const marker = el.marker || el;

        let markerLabel = "";

        // 1) Prefer root-level label (e.g. "Display Home 101")
        if (el.label) {
          markerLabel = el.label;
        }
        // 2) Then try displayName (for living-option / subsection markers)
        else if (el.displayName) {
          markerLabel = el.displayName[langCode] || el.displayName.en || "";
        }
        // 3) Then any label on the marker object itself
        else if (marker.label) {
          markerLabel = marker.label;
        }
        // 4) Fallback: coordinates
        else if (marker.x != null && marker.y != null) {
          markerLabel = `(${marker.x}, ${marker.y})`;
        }
        // 5) Last resort: id
        else {
          markerLabel = el._id;
        }

        parts.push("[MARKER] " + markerLabel);
      }

      // --- POLYGON (uses parent object label) ---
      if (type === "polygon") {
        let polygonLabel = "";

        // activeElement._id matches the parent element _id
        const parentId = this.activeElement && this.activeElement._id;
        const parent = (this.allElements || []).find(
          (item) => item && String(item._id) === String(parentId)
        );

        if (parent) {
          // Try displayName first (if i18n)
          if (parent.displayName) {
            polygonLabel =
              parent.displayName[langCode] || parent.displayName.en || "";
          }

          // Then label (string or i18n object)
          if (!polygonLabel && parent.label) {
            if (typeof parent.label === "string") {
              polygonLabel = parent.label;
            } else {
              polygonLabel = parent.label[langCode] || parent.label.en || "";
            }
          }

          // Then name as a fallback
          if (!polygonLabel && parent.name) {
            polygonLabel = parent.name;
          }
        }

        if (!polygonLabel) {
          polygonLabel = this.activeElement._id;
        }

        parts.push("[POLYGON] " + polygonLabel);
      }

      return parts.join(" > ");
    },

    allElements() {
      let elements = [];

      if (this.page) {
        this.page.interactiveSlides.forEach((slide) => {
          elements.push(...slide.tags);
        });
        elements.push(...this.page.tags);
      }

      if (this.tags) {
        elements.push(...this.tags);
      }

      if (this.properties) {
        elements.push(...this.properties);
      }

      if (this.subsections) {
        elements.push(...this.subsections);
      }

      if (this.livingOptions) {
        elements.push(...this.livingOptions);
      }

      return elements;
    },
  },

  watch: {
    selectedElements(newSelectedElements) {
      if (newSelectedElements.length === 1) {
        window.addEventListener("keydown", this.arrowKeys);
      } else {
        window.removeEventListener("keydown", this.arrowKeys);
      }
    },

    page: {
      deep: true,
      immediate: true,
      handler() {
        this.seedColourHistory();
      },
    },

    tags: {
      deep: true,
      immediate: true,
      handler() {
        this.seedColourHistory();
      },
    },

    properties: {
      deep: true,
      immediate: true,
      handler() {
        this.seedColourHistory();
      },
    },

    subsections: {
      deep: true,
      immediate: true,
      handler() {
        this.seedColourHistory();
      },
    },

    livingOptions: {
      deep: true,
      immediate: true,
      handler() {
        this.seedColourHistory();

        // close menu whenever current selection isn't eligible to edit
        if (!this.isEditMenuActive && this.isMenuOpen) {
          this.$store.commit("draggable/setIsMenuOpen", false);
        }
      },
    },
  },

  mounted() {
    document.addEventListener("keydown", this.handleKeydown);
  },

  methods: {
    assetType(asset) {
      if (asset) {
        const assetExt = asset.split(".").pop();
        const videoTypes = ["mp4", "mov"];

        if (videoTypes.includes(assetExt)) {
          return "video";
        } else {
          return "image";
        }
      }

      return "";
    },

    defaultPolygon() {
      return {
        fill: "rgba(0,0,0,0)",
        stroke: "rgba(255,255,255,0)",
        strokeWidth: 8,
        points: [],
        dropShadow: false,
      };
    },

    ensurePolygon(target) {
      if (!target || typeof target !== "object") return this.defaultPolygon();

      if (!target.polygon || typeof target.polygon !== "object") {
        this.$set(target, "polygon", this.defaultPolygon());
      }

      const defaults = this.defaultPolygon();
      Object.keys(defaults).forEach((key) => {
        if (typeof target.polygon[key] === "undefined") {
          this.$set(target.polygon, key, defaults[key]);
        }
      });

      if (!Array.isArray(target.polygon.points)) {
        this.$set(target.polygon, "points", []);
      }

      return target.polygon;
    },

    deselectElement() {
      this.$store.commit("draggable/deselectActiveElement");
    },

    deselectAll() {
      this.$store.commit("draggable/deselectAll");
    },

    selectAllOfType(sampleTag) {
      if (!Array.isArray(this.tags) || !sampleTag) return;

      const type = sampleTag.type;
      if (!type) return;

      // grab all tags of the same type
      const sameTypeTags = this.tags.filter((tag) => tag.type === type);

      if (!sameTypeTags.length) return;

      this.$store.commit("draggable/setSelectedElements", sameTypeTags);
    },

    selectAllMarkers() {
      // TOFIX ************
      let markers = [];

      // mirror marker sources from LivingOptionMarkers
      if (this.propertyIds && this.properties) {
        markers = markers.concat(
          this.properties.filter((p) => this.propertyIds.includes(p._id))
        );
      }

      if (this.subsectionIds && this.subsections) {
        markers = markers.concat(
          this.subsections.filter((s) => this.subsectionIds.includes(s._id))
        );
      }

      if (this.livingOptionIds && this.livingOptions) {
        markers = markers.concat(
          this.livingOptions.filter(
            (lo) => this.livingOptionIds.includes(lo._id) && lo.active
          )
        );
      }

      if (!markers.length) return;

      // update multi-select state
      this.$store.commit("draggable/setSelectedElements", markers);
    },

    findTagCollection(tagId) {
      if (!tagId) return null;

      // look in interactive slide tags (page.interactiveSlides[*].tags)
      if (this.page && Array.isArray(this.page.interactiveSlides)) {
        for (const slide of this.page.interactiveSlides) {
          if (!Array.isArray(slide.tags)) continue;
          const index = slide.tags.findIndex((t) => t && t._id === tagId);
          if (index !== -1) {
            return {
              collection: slide.tags,
              index,
              scope: { type: "slide", slideId: slide._id },
            };
          }
        }
      }

      // look in the top-level tags prop (living options / subsections etc.)
      if (Array.isArray(this.tags)) {
        const index = this.tags.findIndex((t) => t && t._id === tagId);
        if (index !== -1) {
          return {
            collection: this.tags,
            index,
            scope: { type: "root" },
          };
        }
      }

      return null;
    },

    duplicateTag(tag) {
      if (!tag || !tag._id) return;

      const found = this.findTagCollection(tag._id);
      if (!found) return;

      const { collection, index } = found;
      const source = collection[index];
      if (!source) return;

      // deep clone
      const clone = JSON.parse(JSON.stringify(source));

      // Generate new ObjectId for the clone
      clone._id = new ObjectId().toString();

      // offset position so duplicate isn't directly on top
      if (clone.pos) {
        clone.pos.x += 40;
        clone.pos.y += 40;
      }

      // insert right after the source tag
      collection.splice(index + 1, 0, clone);

      // select the new tag so TagMenu edits the duplicate
      this.$store.commit("draggable/addToSelectedElements", {
        element: clone,
        elementType: "tag",
        isSingle: true,
      });
    },

    removeTag(tagOrId) {
      const tagId =
        typeof tagOrId === "string" ? tagOrId : tagOrId && tagOrId._id;
      if (!tagId) return;

      const found = this.findTagCollection(tagId);
      if (!found) return;

      const { collection, index, scope } = found;
      if (index === -1) return;

      const tag = collection[index];
      const tagClone = JSON.parse(JSON.stringify(tag));

      // Only record undo history for top-level tags (existing behaviour).
      // Slide tags will be removed without undo for now.
      if (scope.type === "root") {
        if (this.actions.length > this.actionIndex + 1) {
          this.actions.splice(this.actionIndex + 1);
        }

        this.actions.push({
          type: "delete-tags",
          items: [
            {
              id: tagClone._id,
              indexBefore: index,
              valueBefore: tagClone,
            },
          ],
        });

        this.actionIndex = this.actions.length - 1;
      }

      // actually delete from the correct array
      collection.splice(index, 1);

      this.$store.commit("draggable/deselectActiveElement");
    },

    findMarkerParent(markerElement) {
      if (!markerElement || !markerElement.parentId) return null;

      const sources =
        markerElement.markerSourceType === "subsection"
          ? this.subsections
          : markerElement.markerSourceType === "livingOption"
          ? this.livingOptions
          : [];

      if (!Array.isArray(sources)) return null;

      return sources.find(
        (item) => item && String(item._id) === String(markerElement.parentId)
      );
    },

    duplicateMarker() {
      if (!this.getActiveMarker) return;

      const marker = this.activeElement.markers[this.markerIndex];

      const clone = JSON.parse(JSON.stringify(marker));
      clone.x = Math.round((clone.x || 0) + 40);
      clone.y = Math.round((clone.y || 0) + 40);

      this.activeElement.markers.push(clone);

      const clonedMarker =
        this.activeElement.markers[this.activeElement.markers.length - 1];

      this.$store.commit("draggable/addToSelectedElements", {
        element: this.activeElement,
        elementType: "marker",
        markerIndex: this.activeElement.markers.length - 1,
        isSingle: true,
      });
    },

    removeMarker() {
      if (!this.getActiveMarker) return;
      if (this.activeElement.markers.length < 2) return;

      this.activeElement.markers.splice(this.markerIndex, 1);

      this.$store.commit("draggable/deselectAll");
    },

    removeSlide(slideId) {
      const slide =
        this.page &&
        Array.isArray(this.page.interactiveSlides) &&
        this.page.interactiveSlides.find((s) => s._id === slideId);

      if (!slide) return;

      this.deleteSlideWithHistory(slide);
    },

    // multi-select delete
    deleteSelectedTags() {
      if (!Array.isArray(this.tags)) return;

      // identify which elements to delete
      let selected = this.selectedElements || [];

      // fallback: if somehow nothing in selectedElements but a tag is active,
      // treat the active tag as the selection
      if (
        (!selected.length || !Array.isArray(selected)) &&
        this.activeElementType === "tag" &&
        this.activeElement &&
        this.activeElement._id
      ) {
        selected = [this.activeElement];
      }

      const idsToDelete = new Set(
        selected.filter((el) => el && el._id).map((el) => el._id)
      );

      if (!idsToDelete.size) return;

      // build undo payload: original value + index for each deleted tag
      const deletedItems = [];
      this.tags.forEach((tag, index) => {
        if (tag && idsToDelete.has(tag._id)) {
          deletedItems.push({
            id: tag._id,
            indexBefore: index,
            valueBefore: JSON.parse(JSON.stringify(tag)),
          });
        }
      });

      if (!deletedItems.length) return;

      // trim future actions if we’re not at the end (standard undo pattern)
      if (this.actions.length > this.actionIndex + 1) {
        this.actions.splice(this.actionIndex + 1);
      }

      // push a single compound action covering all deletions
      this.actions.push({
        type: "delete-tags",
        items: deletedItems,
      });

      this.actionIndex = this.actions.length - 1;

      // actually delete the tags
      for (let i = this.tags.length - 1; i >= 0; i--) {
        const tag = this.tags[i];
        if (tag && idsToDelete.has(tag._id)) {
          this.tags.splice(i, 1);
        }
      }

      this.deselectElement();
    },

    deleteSlideWithHistory(slide) {
      if (!slide || !slide._id) return;
      if (!this.page || !Array.isArray(this.page.interactiveSlides)) return;

      const index = this.page.interactiveSlides.findIndex(
        (s) => s._id === slide._id
      );
      if (index === -1) return;

      const snapshot = JSON.parse(
        JSON.stringify(this.page.interactiveSlides[index])
      );

      // trim future actions if we’re not at the end of the history
      if (this.actions.length > this.actionIndex + 1) {
        this.actions.splice(this.actionIndex + 1);
      }

      this.actions.push({
        type: "delete-slide",
        item: {
          id: snapshot._id,
          indexBefore: index,
          valueBefore: snapshot,
        },
      });

      this.actionIndex = this.actions.length - 1;

      // actually remove the slide
      this.page.interactiveSlides.splice(index, 1);

      // clear selection so UI closes nicely
      this.$store.commit("draggable/setActiveSlide", null);
      this.$store.commit("draggable/deselectAll");
    },

    handleKeydown(event) {
      // don't trigger the zoom / delete if input is focused
      const inputs = ["input", "textarea"];

      if (!event) {
        return false;
      }

      if (
        document.activeElement &&
        inputs.includes(document.activeElement.tagName.toLowerCase())
      ) {
        return false;
      }

      // delete / backspace
      if (event.key === "Delete" || event.key === "Backspace") {
        // if on a tag and have a selection, use the multi-delete path (with undo)
        if (this.activeElementType === "tag") {
          this.deleteSelectedTags();
          event.preventDefault(); // prevents default shortcuts being used (e.g. backspace to go previous page)
          return;
        }

        // otherwise fall back to the single-delete logic (e.g. slides)
        this.handleDeleteShortcut();
        event.preventDefault();
        return;
      }

      if (event.key === "+") {
        this.zoomIn();
      }

      if (event.key === "_") {
        this.zoomOut();
      }

      if (event.key === ")") {
        this.zoomReset();
      }

      if (event.key === "Escape") {
        this.deselectAll();
      }
    },

    zoomIn() {
      if (this.zoomLevel < this.maxZoomLevel) {
        this.zoomLevel += 1;
      }
    },

    zoomOut() {
      if (this.zoomLevel > 1) {
        this.zoomLevel -= 1;
      }
    },

    zoomReset() {
      this.zoomLevel = 1;
    },

    handleDeleteShortcut() {
      // no active thing = nothing to delete
      if (!this.activeElementType || !this.activeElement) return;

      // delete tag with undo/redo support
      if (this.activeElementType === "tag") {
        this.deleteSelectedTags(); // multi-select aware
        return;
      }

      // delete slide with undo/redo support
      if (this.activeElementType === "slide") {
        this.deleteSlideWithHistory(this.activeElement);
        return;
      }

      // (marker / polygon support can be added later)
    },

    startPolygon() {
      if (!this.activeElement) return false;
      if (
        this.activeElement.polygon &&
        this.activeElement.polygon.points.length
      ) {
        return false;
      }

      this.$store.commit("draggable/startPolygon", this.activeElement._id);
    },

    editPolygon() {
      if (!this.activeElement) return false;

      let id =
        this.activeElementType === "polygon"
          ? this.activeElement.tagId
          : this.activeElement._id;

      this.$store.commit("draggable/editPolygon", id);
    },

    deletePolygon() {
      if (!this.activeElement) return false;

      this.$store.commit("draggable/deletePolygon", this.activeElement._id);
    },

    applyPolygonSnapshot(id, snapshot) {
      const parent = (this.allElements || []).find(
        (p) => p && String(p._id) === String(id)
      );
      if (!parent) return;

      // ensure polygon object exists, then overwrite
      const polygon = this.ensurePolygon(parent);
      Object.assign(polygon, JSON.parse(JSON.stringify(snapshot.polygon)));
    },

    recordAction(after) {
      // POLYGON undo/redo
      if (after && after.elementType === "polygon") {
        // must have a valid before snapshot
        if (!this.beforeAction || !this.beforeAction._id) return;

        // must be the same element
        if (this.beforeAction._id !== after._id) return;

        const afterClean = JSON.parse(JSON.stringify(after));
        const beforeClean = JSON.parse(JSON.stringify(this.beforeAction || {}));

        if (_.isEqual(beforeClean, afterClean)) return;

        if (this.actions.length > this.actionIndex + 1) {
          this.actions.splice(this.actionIndex + 1);
        }

        this.actions.push({
          type: "polygon",
          id: beforeClean._id,
          valueBefore: beforeClean,
          valueAfter: afterClean,
        });

        this.updateBeforeAction(afterClean);
        this.actionIndex++;
        return;
      }

      // --- tag / marker ---

      // don't record if nothing has changed
      const selectedElement = JSON.parse(JSON.stringify(after));
      delete selectedElement.elementType;

      if (_.isEqual(this.beforeAction, selectedElement)) {
        return;
      }

      // if you record a new action and you're not at the end
      // of the action list -> remove all subsequent actions
      if (this.actions.length > this.actionIndex + 1) {
        this.actions.splice(this.actionIndex + 1);
      }

      this.actions.push({
        type: "tag",
        id: this.beforeAction._id,
        valueBefore: JSON.parse(JSON.stringify(this.beforeAction)),
        valueAfter: JSON.parse(JSON.stringify(after)),
      });

      this.updateBeforeAction();

      this.actionIndex++;
    },

    updateBeforeAction(element = this.activeElement) {
      this.beforeAction = JSON.parse(JSON.stringify(element));
    },

    alignHorizontal() {
      if (this.selectedElements.length < 2) return;

      const xPositions = this.selectedElements.map((element) => {
        if (element.pos) return element.pos.x;
        if (element.marker) return element.marker.x;
      });

      const averageX =
        xPositions.reduce((a, b) => a + b, 0) / this.selectedElements.length;
      const averageXFloored = Math.floor(averageX);

      this.selectedElements.forEach((element) => {
        const elementBefore = JSON.parse(JSON.stringify(element));
        this.updateBeforeAction(elementBefore);
        if (element.pos) element.pos.x = averageXFloored;
        if (element.marker) element.marker.x = averageXFloored;
        this.recordAction(element);
      });
    },

    alignVertical() {
      if (this.selectedElements.length < 2) return;

      const yPositions = this.selectedElements.map((element) => {
        if (element.pos) return element.pos.y;
        if (element.marker) return element.marker.y;
      });

      const averageY =
        yPositions.reduce((a, b) => a + b, 0) / this.selectedElements.length;
      const averageYFloored = Math.floor(averageY);

      this.selectedElements.forEach((element) => {
        const elementBefore = JSON.parse(JSON.stringify(element));
        this.updateBeforeAction(elementBefore);
        if (element.pos) element.pos.y = averageYFloored;
        if (element.marker) element.marker.y = averageYFloored;
        this.recordAction(element);
      });
    },

    distributeHorizontal() {
      if (this.selectedElements.length < 3) return;

      const sorted = this.selectedElements.sort((a, b) => a.pos.x - b.pos.x);
      const lowest = sorted[0];
      const highest = sorted[this.selectedElements.length - 1];
      const range = highest.pos.x - lowest.pos.x;
      const steps = this.selectedElements.length - 1;
      const stepSize = range / steps;

      this.selectedElements.forEach((element, i) => {
        if (i !== 0 && i !== this.selectedElements.length - 1) {
          const elementBefore = JSON.parse(JSON.stringify(element));
          this.updateBeforeAction(elementBefore);
          element.pos.x = Math.floor(lowest.pos.x + stepSize * i);
          this.recordAction(element);
        }
      });
    },

    distributeVertical() {
      if (this.selectedElements.length < 3) return;

      const sorted = this.selectedElements.sort((a, b) => a.pos.y - b.pos.y);
      const lowest = sorted[0];
      const highest = sorted[this.selectedElements.length - 1];
      const range = highest.pos.y - lowest.pos.y;
      const steps = this.selectedElements.length - 1;
      const stepSize = range / steps;

      this.selectedElements.forEach((element, i) => {
        if (i !== 0 && i !== this.selectedElements.length - 1) {
          const elementBefore = JSON.parse(JSON.stringify(element));
          this.updateBeforeAction(elementBefore);
          element.pos.y = Math.floor(lowest.pos.y + stepSize * i);
          this.recordAction(element);
        }
      });
    },

    actionUndo() {
      if (this.actionIndex < 0) return;

      const lastAction = this.actions[this.actionIndex];

      // 🔹 Undo property edits for a single tag OR marker
      if (lastAction.type === "tag") {
        // 1) Try normal tags
        if (Array.isArray(this.allElements)) {
          const tag = this.allElements.find((tag) => tag._id === lastAction.id);
          if (tag) {
            Object.assign(tag, lastAction.valueBefore);
          }
        }
      }

      // 🔹 Undo delete of one or more tags
      if (lastAction.type === "delete-tags") {
        lastAction.items.forEach((item) => {
          const existingIndex = this.tags.findIndex((t) => t._id === item.id);

          if (existingIndex !== -1) {
            Object.assign(this.tags[existingIndex], item.valueBefore);
            return;
          }

          const clone = JSON.parse(JSON.stringify(item.valueBefore));
          const targetIndex =
            typeof item.indexBefore === "number"
              ? Math.min(item.indexBefore, this.tags.length)
              : this.tags.length;

          this.tags.splice(targetIndex, 0, clone);
        });
      }

      // Undo delete of a slide
      if (lastAction.type === "delete-slide") {
        if (!this.page || !Array.isArray(this.page.interactiveSlides)) {
          this.actionIndex--;
          return;
        }

        const item = lastAction.item;
        const existingIndex = this.page.interactiveSlides.findIndex(
          (s) => s._id === item.id
        );

        if (existingIndex !== -1) {
          Object.assign(
            this.page.interactiveSlides[existingIndex],
            item.valueBefore
          );
        } else {
          const clone = JSON.parse(JSON.stringify(item.valueBefore));
          const targetIndex =
            typeof item.indexBefore === "number"
              ? Math.min(item.indexBefore, this.page.interactiveSlides.length)
              : this.page.interactiveSlides.length;

          this.page.interactiveSlides.splice(targetIndex, 0, clone);
        }
      }

      if (lastAction.type === "polygon") {
        this.applyPolygonSnapshot(lastAction.id, lastAction.valueBefore);
      }

      this.actionIndex--;
    },

    actionRedo() {
      if (this.actionIndex >= this.actions.length - 1) return;

      this.actionIndex++;
      const nextAction = this.actions[this.actionIndex];

      // Redo property edits for a single tag OR marker
      if (nextAction.type === "tag") {
        // 1) Try normal tags
        if (Array.isArray(this.allElements)) {
          const tag = this.allElements.find((tag) => tag._id === nextAction.id);
          if (tag) {
            Object.assign(tag, nextAction.valueAfter);
          }
        }
      }

      //  Redo delete of one or more tags
      if (nextAction.type === "delete-tags") {
        const ids = new Set(nextAction.items.map((item) => item.id));

        for (let i = this.tags.length - 1; i >= 0; i--) {
          const tag = this.tags[i];
          if (tag && ids.has(tag._id)) {
            this.tags.splice(i, 1);
          }
        }
      }

      //  Redo delete of a slide
      if (nextAction.type === "delete-slide") {
        if (!this.page || !Array.isArray(this.page.interactiveSlides)) return;

        const id = nextAction.item && nextAction.item.id;
        if (!id) return;

        const idx = this.page.interactiveSlides.findIndex((s) => s._id === id);
        if (idx !== -1) {
          this.page.interactiveSlides.splice(idx, 1);
        }
      }

      if (nextAction.type === "polygon") {
        this.applyPolygonSnapshot(nextAction.id, nextAction.valueAfter);
      }
    },

    arrowKeys(event) {
      // ignore if focused on input field
      const focusedTagType = document.activeElement.tagName.toLowerCase();
      const ignoreTags = ["input", "textarea"];

      if (ignoreTags.includes(focusedTagType)) return;

      // only respond to arrow keys
      const arrowKeys = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"];
      if (!arrowKeys.includes(event.key)) return;

      const element = this.selectedElements[0];
      if (!element) return;

      const hasPos =
        element.pos &&
        typeof element.pos.x === "number" &&
        typeof element.pos.y === "number";

      const hasMarker =
        element.marker &&
        typeof element.marker.x === "number" &&
        typeof element.marker.y === "number";

      if (!hasPos && !hasMarker) return;

      let dx = 0;
      let dy = 0;

      switch (event.key) {
        case "ArrowLeft":
          dx = -1;
          break;
        case "ArrowRight":
          dx = 1;
          break;
        case "ArrowUp":
          dy = -1;
          break;
        case "ArrowDown":
          dy = 1;
          break;
      }

      // nothing to do
      if (dx === 0 && dy === 0) return;

      // prevent the page / scroll container moving
      event.preventDefault();

      if (hasPos) {
        element.pos.x += dx;
        element.pos.y += dy;
      } else if (hasMarker) {
        element.marker.x += dx;
        element.marker.y += dy;
      }
    },

    seedColourHistory() {
      const normalize = (c) => {
        if (!c) return null;
        const str = String(c).trim().toLowerCase();

        // hex (#rgb or #rrggbb)
        if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/.test(str)) return str;

        // rgb() or rgba()
        if (
          /^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/.test(
            str
          )
        )
          return str;

        return null;
      };

      const colours = new Set();

      // tags (page-level)
      const pageTags =
        this.page && Array.isArray(this.page.tags) ? this.page.tags : [];

      pageTags.forEach((tag) => {
        [tag && tag.backgroundColour, tag && tag.textColour]
          .map(normalize)
          .filter(Boolean)
          .forEach((c) => colours.add(c));
      });

      // tags (root / living options canvas)
      const rootTags = Array.isArray(this.tags) ? this.tags : [];

      rootTags.forEach((tag) => {
        [tag && tag.backgroundColour, tag && tag.textColour]
          .map(normalize)
          .filter(Boolean)
          .forEach((c) => colours.add(c));
      });

      // slide tags
      const slides =
        this.page && Array.isArray(this.page.interactiveSlides)
          ? this.page.interactiveSlides
          : [];

      slides.forEach((slide) => {
        const slideTags = slide && Array.isArray(slide.tags) ? slide.tags : [];
        slideTags.forEach((tag) => {
          [tag && tag.backgroundColour, tag && tag.textColour]
            .map(normalize)
            .filter(Boolean)
            .forEach((c) => colours.add(c));
        });
      });

      // shared sources for polygons + markers
      const sources = []
        .concat(Array.isArray(this.properties) ? this.properties : [])
        .concat(Array.isArray(this.subsections) ? this.subsections : [])
        .concat(Array.isArray(this.livingOptions) ? this.livingOptions : []);

      // polygons
      sources.forEach((item) => {
        const p = item && item.polygon;
        if (!p) return;

        [p.fill, p.stroke]
          .map(normalize)
          .filter(Boolean)
          .forEach((c) => colours.add(c));
      });

      // markers
      sources.forEach((item) => {
        const m = item && item.marker;
        if (!m) return;

        [m.backgroundColour, m.textColour]
          .map(normalize)
          .filter(Boolean)
          .forEach((c) => colours.add(c));
      });

      // limit to 6 sample colours
      this.sharedColourHistory = Array.from(colours).slice(0, 6);
    },
  },

  beforeDestroy() {
    document.removeEventListener("keydown", this.handleKeydown);
    window.removeEventListener("keydown", this.arrowKeys);
  },
};
</script>

<style lang="scss" scoped>
.controls-container {
  overflow: hidden;
  position: relative;

  &.active-slide {
    outline: 5px solid #28a745;
  }

  &.active-tag {
    outline: 5px solid #3182ce;
  }

  &.active-marker {
    outline: 5px solid #d9534f;
  }

  &.active-polygon {
    outline: 5px solid #8b40ff;
  }
}

.zoom-container {
  aspect-ratio: 3190/1960;
  overflow: scroll;
}

.draggable-container {
  aspect-ratio: 3190/1960;
  overflow: hidden;
  pointer-events: all;
  position: relative;
  z-index: 1000;

  &--zoom {
    &-2 {
      width: 200%;
    }
    &-3 {
      width: 400%;
    }
    &-4 {
      width: 600%;
    }
  }

  &__bg {
    height: 100%;
    pointer-events: all;
    position: relative;
    user-select: none;

    img {
      height: 100%;
      object-fit: cover;
      width: 100%;
    }
  }
}

.no-image {
  background-image: linear-gradient(
      45deg,
      #eee 25%,
      transparent 25%,
      transparent 75%,
      #eee 75%,
      #eee 100%
    ),
    linear-gradient(45deg, #eee 25%, white 25%, white 75%, #eee 75%, #eee 100%);
  background-position: 0px 0px, 10px 10px;
  background-size: 20px 20px;
}

.tag-bg {
  height: 100%;
  object-fit: cover;
  pointer-events: none;
  width: 100%;

  &.hidden {
    opacity: 0;
  }
}

.dot-status {
  background-color: rgba(#fff, 0.7);
  color: #000;
  font-size: 16px;
  padding: 10px;
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 6;

  input.form-control {
    display: inline-block;
    padding: 0 5px;
    width: 60px;
  }
}

.active-banner {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  padding: 3px 8px;
  border-radius: 3px;
  font-size: 14px;
  font-weight: 500;
  color: #fff;
  z-index: 5000;
  white-space: nowrap;
  background: #3182ce; // default, overridden below
}

.active-banner--tag {
  background: #3182ce; // blue
}

.active-banner--slide {
  background: #28a745; // green
}

.active-banner--marker {
  background: #d9534f; // red
}

.active-banner--polygon {
  background: #8b40ff; // purple
}
</style>
